import React from 'react';

import type { User } from '../../../../../../@types/User';
import type { DashboardExactDates, DashboardPersonAssignments } from '../../../../../../store/dashboardSlice';
import type { Module, UserSection as UserSectionType } from '../../types';
import ModuleCard from '../ModuleCard';

interface UserSectionProps {
  section: UserSectionType;
  onToggleSection: (sectionId: string) => void;
  onOpenRoleModal: (sectionId: string) => void;
  onModuleClick: (module: Module) => void;
  onShowTooltip: (event: React.MouseEvent, module: Module) => void;
  onHideTooltip: () => void;
  headerAction?: React.ReactNode;
  personAssignments?: DashboardPersonAssignments;
  exactDates?: DashboardExactDates;
  companyUsers?: User[];
  restrictedToRowKeys?: string[];
}

function UserSection({
  section,
  onToggleSection,
  onOpenRoleModal,
  onModuleClick,
  onShowTooltip,
  onHideTooltip,
  headerAction,
  personAssignments,
  exactDates,
  companyUsers,
  restrictedToRowKeys,
}: UserSectionProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50">
      {/* Section Header */}
      <div 
        className="cursor-pointer border-b border-gray-300 p-4 transition-all duration-300 hover:bg-gray-100 hover:shadow-md"
        onClick={() => onToggleSection(section.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-3">
            <span className="text-lg">{section.icon}</span>
            <h4 className="text-sm font-bold text-rgpd-blue">{section.title}</h4>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="rounded-lg bg-rgpd-green px-3 py-1.5 text-xs font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-rgpd-green-dark hover:shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                onOpenRoleModal(section.id);
              }}
            >
              <span role="img" aria-label="Book">📖</span> RÔLE / MISSION
            </button>
            <span className="rounded-full bg-rgpd-blue px-3 py-1.5 text-xs font-semibold text-white">
              {section.modules.length} module{section.modules.length > 1 ? 's' : ''}
            </span>
            <span className={`text-rgpd-blue transition-transform duration-300 ${section.collapsed ? '-rotate-90' : ''}`}>
              ▼
            </span>
          </div>
        </div>
      </div>

      {/* Section Modules */}
      <div className={`transition-all duration-400 ease-in-out ${
        section.collapsed 
          ? 'max-h-0 overflow-hidden opacity-0' 
          : 'overflow-visible opacity-100'
      }`}>
        <div className="space-y-3 p-4">
          {headerAction}
          {section.modules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              onModuleClick={onModuleClick}
              onShowTooltip={onShowTooltip}
              onHideTooltip={onHideTooltip}
              personAssignments={personAssignments}
              exactDates={exactDates}
              companyUsers={companyUsers}
              restrictedToRowKeys={restrictedToRowKeys}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default UserSection;
